/**
 * 郵便番号入力の端末内履歴（MRU 最大5件・お預かり from / お届け to で別キー）
 */

export type YhcZipHistorySlot = 'from' | 'to'

export type YhcZipHistoryEntry = {
  zip: string
  address: string
  usedAt: number
}

const MAX_ENTRIES = 5

export function getStorageKey(slot: YhcZipHistorySlot): string {
  return slot === 'from' ? 'yhc:zipHistory:from' : 'yhc:zipHistory:to'
}

function isValidEntry(x: unknown): x is YhcZipHistoryEntry {
  if (!x || typeof x !== 'object') return false
  const o = x as Record<string, unknown>
  return (
    typeof o.zip === 'string' &&
    typeof o.address === 'string' &&
    typeof o.usedAt === 'number'
  )
}

function parseEntries(raw: string | null): YhcZipHistoryEntry[] {
  if (!raw) return []
  try {
    const p = JSON.parse(raw) as unknown
    if (!Array.isArray(p)) return []
    return p.filter(isValidEntry)
  } catch {
    return []
  }
}

function readEntries(key: string): YhcZipHistoryEntry[] {
  try {
    return parseEntries(localStorage.getItem(key))
  } catch {
    return []
  }
}

function writeEntries(key: string, entries: YhcZipHistoryEntry[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(entries))
  } catch {
    // 容量超過・プライベートモード等
  }
}

/** 正規化した7桁郵便番号（全角数字→半角、数字以外除去・先頭7桁） */
export function normalizeZipDigits(zip: string): string {
  const half = zip.replace(/[０-９]/g, (s) =>
    String.fromCharCode(s.charCodeAt(0) - 0xfee0),
  )
  return half.replace(/\D/g, '').slice(0, 7)
}

/**
 * 履歴を読み込み（MRU 最大 MAX_ENTRIES 件）
 */
export function loadZipCodeHistory(slot: YhcZipHistorySlot): YhcZipHistoryEntry[] {
  const key = getStorageKey(slot)
  const entries = readEntries(key)
  return [...entries].sort((a, b) => b.usedAt - a.usedAt).slice(0, MAX_ENTRIES)
}

/**
 * 有効な郵便番号・住所が揃ったときに履歴へ追加（同一 zip は先頭へ寄せて usedAt 更新）
 */
export function recordZipCodeHistory(
  slot: YhcZipHistorySlot,
  zip: string,
  address: string,
): void {
  const normalizedZip = normalizeZipDigits(zip)
  if (normalizedZip.length !== 7) return
  const trimmedAddress = address.trim()
  if (!trimmedAddress) return

  const key = getStorageKey(slot)
  const rest = readEntries(key).filter((e) => e.zip !== normalizedZip)
  const next: YhcZipHistoryEntry[] = [
    { zip: normalizedZip, address: trimmedAddress, usedAt: Date.now() },
    ...rest,
  ].slice(0, MAX_ENTRIES)
  writeEntries(key, next)
}
